import { Injectable, isDevMode } from '@angular/core';
import { AdminAuthStorageKeys } from '../../enums/admin-auth-storage-keys.enum';
import { safeErrorSummary } from '../../helper/safe-error-summary';

/**
 * Service for encrypting and decrypting data stored in localStorage
 * Uses Web Crypto API for secure encryption
 */
@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // 96 bits for AES-GCM

  /**
   * Dev-only logging. Suppressed in production builds so the plaintext data
   * being encrypted is never written to the browser console of a deployed app.
   */
  private debug(...args: unknown[]): void {
    if (isDevMode()) {
      console.warn(...args);
    }
  }

  /**
   * Generates a cryptographic key for encryption/decryption
   */
  private async generateKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH,
      },
      true, // extractable
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Derives a key from a password using PBKDF2
   */
  private async deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(password), { name: 'PBKDF2' }, false, [
      'deriveKey',
    ]);

    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // 100k iterations for security
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: this.ALGORITHM, length: this.KEY_LENGTH },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Gets or creates a session-based encryption key
   */
  private async getSessionKey(): Promise<CryptoKey> {
    const sessionKeyData = sessionStorage.getItem(AdminAuthStorageKeys.SESSION_KEY);

    if (sessionKeyData) {
      try {
        const keyData = JSON.parse(sessionKeyData);
        return await crypto.subtle.importKey('raw', new Uint8Array(keyData), { name: this.ALGORITHM }, false, [
          'encrypt',
          'decrypt',
        ]);
      } catch {
        // If key import fails, generate new key
      }
    }

    // Generate new key and store in session
    const key = await this.generateKey();
    const exportedKey = await crypto.subtle.exportKey('raw', key);
    sessionStorage.setItem(AdminAuthStorageKeys.SESSION_KEY, JSON.stringify(Array.from(new Uint8Array(exportedKey))));

    return key;
  }

  /**
   * Encrypts data and stores it in localStorage
   */
  async setSecureItem<T>(key: string, data: T): Promise<void> {
    try {
      this.debug('SecureStorageService: Starting encryption for key:', key);
      // #2249: never print the plaintext being encrypted — the whole point of this service.

      const encoder = new TextEncoder();
      const dataString = JSON.stringify(data);
      const dataBuffer = encoder.encode(dataString);

      this.debug('SecureStorageService: Getting session key...');
      const cryptoKey = await this.getSessionKey();
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
        },
        cryptoKey,
        dataBuffer,
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Store as base64
      const base64Data = btoa(String.fromCharCode(...combined));
      localStorage.setItem(key, base64Data);
      this.debug('SecureStorageService: Successfully encrypted and stored data for key:', key);
    } catch (error) {
      console.error('SecureStorageService: Error encrypting data:', safeErrorSummary(error));
      throw new Error('Failed to encrypt and store data');
    }
  }

  /**
   * Retrieves and decrypts data from localStorage
   */
  async getSecureItem<T>(key: string): Promise<T | null> {
    try {
      const encryptedData = localStorage.getItem(key);
      if (!encryptedData) {
        return null;
      }

      // Decode from base64
      const combined = new Uint8Array(
        atob(encryptedData)
          .split('')
          .map(char => char.charCodeAt(0)),
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, this.IV_LENGTH);
      const encrypted = combined.slice(this.IV_LENGTH);

      const cryptoKey = await this.getSessionKey();

      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
        },
        cryptoKey,
        encrypted,
      );

      const decoder = new TextDecoder();
      const dataString = decoder.decode(decryptedData);

      return JSON.parse(dataString) as T;
    } catch (error) {
      console.error('Error decrypting data:', safeErrorSummary(error));
      return null;
    }
  }

  /**
   * Removes encrypted item from localStorage
   */
  removeSecureItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Clears the session encryption key (call on logout)
   */
  clearSessionKey(): void {
    sessionStorage.removeItem(AdminAuthStorageKeys.SESSION_KEY);
  }
}
